import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { IconAiEval } from "@/components/SvoIcons";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type EvalRow = Awaited<ReturnType<typeof api.admin.evalStatus>>["perTeam"][number];

const STATUS_LABEL: Record<EvalRow["status"], string> = {
  no_requirement: "Keine Anforderung",
  draft: "Entwurf",
  submitted: "Eingereicht — wartet auf KI",
  evaluated: "Bewertung fertig",
};

const STATUS_KLASS: Record<EvalRow["status"], string> = {
  no_requirement: "status-pill",
  draft: "status-pill",
  submitted: "status-pill is-info",
  evaluated: "status-pill is-good",
};

export function AdminEvalStatusSection() {
  const { state, dispatch } = useStore();
  const [data, setData] = useState<{
    total: number;
    completed: number;
    perTeam: EvalRow[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const reload = async () => {
    try {
      const r = await api.admin.evalStatus(state.auth.token);
      setData(r);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    reload();
    const id = setInterval(reload, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth.token]);

  const doResetEvaluations = async () => {
    setConfirmReset(false);
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.reset(state.auth.token, "evaluations");
      setMsg("Bewertungen archiviert.");
      await reload();
    } catch (err) {
      console.error(err);
      setMsg("Reset fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const trigger = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.evaluations.trigger(state.auth.token);
      const p = await api.phase.set(state.auth.token, "evaluating");
      dispatch({ type: "phase/set", phase: p });
      setMsg("Bewertung gestartet — alle Teams werden parallel bewertet.");
      await reload();
    } catch (err) {
      console.error(err);
      setMsg("Konnte Bewertung nicht starten.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="form-hint">Lade …</p>;

  const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div className="admin-grid">
      <header className="admin-section-head">
        <h2>KI-Bewertung</h2>
        <p>
          Vollständiger Status pro Team. Teilnehmer sehen nur den Fortschrittsbalken + Live-Feed.
        </p>
      </header>

      <div
        className="admin-grid"
        style={{
          padding: "18px 20px",
          background: "var(--svo-bg)",
          borderRadius: "var(--radius-xl)",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <strong style={{ color: "var(--svo-dunkelblau)", fontSize: 16 }}>
            Fortschritt
          </strong>
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontSize: 28,
              fontWeight: 700,
              color: "var(--svo-dunkelblau)",
            }}
          >
            {data.completed} / {data.total}
          </span>
        </div>
        <div className="progress-track">
          <div
            className={`progress-fill${pct >= 100 ? " is-done" : ""}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="admin-toolbar">
          <button
            type="button"
            onClick={trigger}
            disabled={busy}
            className="btn btn-primary"
          >
            <IconAiEval size={16} />
            {busy ? "…" : "Bewertung jetzt starten"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            disabled={busy}
            className="btn btn-warning"
          >
            ↻ Bewertungen zurücksetzen
          </button>
          {msg && (
            <span style={{ color: "var(--svo-dunkelblau)", fontSize: 13 }}>{msg}</span>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {data.perTeam.length === 0 && (
          <p className="form-hint">Noch keine Teams.</p>
        )}
        {data.perTeam.map((row) => (
          <div
            key={row.teamNumber}
            style={{
              padding: "16px 18px",
              borderRadius: "var(--radius-xl)",
              border: "1px solid var(--svo-grau-100)",
              background: "#fff",
              display: "grid",
              gap: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div
                className="badge"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "var(--svo-blau)",
                  color: "var(--svo-weiss)",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 700,
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {row.teamNumber}
              </div>
              <strong
                style={{
                  color: "var(--svo-dunkelblau)",
                  flex: 1,
                  fontSize: 16,
                }}
              >
                {row.teamName ?? `Team ${row.teamNumber}`}
              </strong>
              {row.score !== null && (
                <span
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 700,
                    color: "var(--svo-dunkelblau)",
                    fontSize: 22,
                  }}
                >
                  {row.score.toFixed(1)}
                  <small style={{ fontSize: 12, fontWeight: 500, opacity: 0.6 }}>
                    /10
                  </small>
                </span>
              )}
              <span className={STATUS_KLASS[row.status]}>
                <span className="dot" />
                {STATUS_LABEL[row.status]}
              </span>
            </div>
            {row.zusammenfassung && (
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "var(--svo-grau-500)",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                }}
              >
                „{row.zusammenfassung}"
              </p>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Bewertungen zurücksetzen?"
        message="Alle KI-Bewertungen werden ins Archiv verschoben. Die Anforderungen der Teams bleiben erhalten."
        confirmLabel="Ja, archivieren"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doResetEvaluations}
        onCancel={() => setConfirmReset(false)}
        busy={busy}
      />
    </div>
  );
}
